import React from 'react';

//Custom inline SVG icons for the Recording Info file tree. All share viewBox 0 0 16 16 and
//render at 18px. The typed file icons are one family: a grey page with a folded corner, a
//per-type watermark glyph, and a colour-coded badge carrying the extension text.

const SIZE = 18;

const page = (
    <React.Fragment>
        <path d="M3.6 1.2 h6 L13 4.6 v9.6 a1 1 0 0 1 -1 1 H4.6 a1 1 0 0 1 -1 -1 z"
              fill="#ECEFF1" stroke="#90A4AE" strokeWidth="0.7" strokeLinejoin="round" />
        <path d="M9.6 1.2 L13 4.6 H9.6 z" fill="#B0BEC5" />
    </React.Fragment>
);

const badge = (colour, label) => (
    <React.Fragment>
        <rect x="1" y="8.6" width="14" height="5.6" rx="1.1" fill={colour} />
        <text x="8" y="12.9" textAnchor="middle"
              fontFamily="Arial, Helvetica, sans-serif" fontWeight="bold"
              fontSize={label.length > 3 ? '3.9' : '4.6'} fill="#FFFFFF">{label}</text>
    </React.Fragment>
);

const playWatermark = <path d="M7 4.4 l3 1.8 -3 1.8 z" fill="#78909C" />;

export const FolderClosedIcon = () => (
    <svg className="fileTreeIcon" width={SIZE} height={SIZE} viewBox="0 0 16 16" aria-hidden="true">
        <path d="M1.2 3.4 a1 1 0 0 1 1 -1 h3.8 l1.4 1.7 h6.4 a1 1 0 0 1 1 1 v7.5 a1 1 0 0 1 -1 1 H2.2 a1 1 0 0 1 -1 -1 z"
              fill="#F0B429" />
        <path d="M1.2 5.6 h13.6 v6 a1 1 0 0 1 -1 1 H2.2 a1 1 0 0 1 -1 -1 z" fill="#FFD25F" />
    </svg>
);

export const FolderOpenIcon = () => (
    <svg className="fileTreeIcon" width={SIZE} height={SIZE} viewBox="0 0 16 16" aria-hidden="true">
        <path d="M1.2 3.4 a1 1 0 0 1 1 -1 h3.8 l1.4 1.7 h6.4 a1 1 0 0 1 1 1 v1 H1.2 z"
              fill="#F0B429" />
        <path d="M2.6 6.1 h12.9 a0.8 0.8 0 0 1 0.77 1 l-1.35 5 a1 1 0 0 1 -0.97 0.74 H1.5 a0.6 0.6 0 0 1 -0.58 -0.76 l1.1 -5.3 a0.8 0.8 0 0 1 0.58 -0.68 z"
              fill="#FFD25F" />
    </svg>
);

export const GenericFileIcon = () => (
    <svg className="fileTreeIcon" width={SIZE} height={SIZE} viewBox="0 0 16 16" aria-hidden="true">
        {page}
        <path d="M5.2 7 h5.6 M5.2 9.2 h5.6 M5.2 11.4 h3.6" stroke="#B0BEC5" strokeWidth="0.9" strokeLinecap="round" />
    </svg>
);

export const BagFileIcon = () => (
    <svg className="fileTreeIcon" width={SIZE} height={SIZE} viewBox="0 0 16 16" aria-hidden="true">
        {page}
        {/* data cylinder: a rosbag is recorded sensor data */}
        <ellipse cx="8.3" cy="4.6" rx="2.4" ry="1" fill="none" stroke="#78909C" strokeWidth="0.8" />
        <path d="M5.9 4.6 v2.4 a2.4 1 0 0 0 4.8 0 v-2.4" fill="none" stroke="#78909C" strokeWidth="0.8" />
        {badge('#7B1FA2', 'BAG')}
    </svg>
);

export const YamlFileIcon = () => (
    <svg className="fileTreeIcon" width={SIZE} height={SIZE} viewBox="0 0 16 16" aria-hidden="true">
        {page}
        {/* indented key: value lines */}
        <path d="M5.2 4.4 h2.2 M5.2 6.4 h1.2 M7.4 6.4 h3.2" stroke="#78909C" strokeWidth="0.9" strokeLinecap="round" />
        {badge('#00897B', 'YAML')}
    </svg>
);

export const Mp4FileIcon = () => (
    <svg className="fileTreeIcon" width={SIZE} height={SIZE} viewBox="0 0 16 16" aria-hidden="true">
        {page}
        {playWatermark}
        {badge('#D32F2F', 'MP4')}
    </svg>
);

export const TsFileIcon = () => (
    <svg className="fileTreeIcon" width={SIZE} height={SIZE} viewBox="0 0 16 16" aria-hidden="true">
        {page}
        {playWatermark}
        {badge('#EF6C00', 'TS')}
    </svg>
);

export const LasFileIcon = () => (
    <svg className="fileTreeIcon" width={SIZE} height={SIZE} viewBox="0 0 16 16" aria-hidden="true">
        {page}
        {/* point-cloud scatter: a las is lidar points */}
        <circle cx="5.6" cy="4.4" r="0.7" fill="#78909C" />
        <circle cx="8.1" cy="3.7" r="0.7" fill="#78909C" />
        <circle cx="10.6" cy="4.7" r="0.7" fill="#78909C" />
        <circle cx="6.7" cy="6.3" r="0.7" fill="#78909C" />
        <circle cx="9.4" cy="6.7" r="0.7" fill="#78909C" />
        {badge('#1976D2', 'LAS')}
    </svg>
);

export const PlyFileIcon = () => (
    <svg className="fileTreeIcon" width={SIZE} height={SIZE} viewBox="0 0 16 16" aria-hidden="true">
        {page}
        {/* wireframe mesh: a ply is triangulated geometry / a point cloud */}
        <path d="M5.4 6.8 L8 3.9 L10.8 6.4 z" fill="none" stroke="#78909C" strokeWidth="0.8" strokeLinejoin="round" />
        <circle cx="5.4" cy="6.8" r="0.8" fill="#78909C" />
        <circle cx="8" cy="3.9" r="0.8" fill="#78909C" />
        <circle cx="10.8" cy="6.4" r="0.8" fill="#78909C" />
        {badge('#C2185B', 'PLY')}
    </svg>
);

export const CaretIcon = () => (
    <svg className="treeCaret" width="12" height="12" viewBox="0 0 16 16" aria-hidden="true">
        <path d="M5 2.5 l6 5.5 -6 5.5 z" fill="#606060" />
    </svg>
);

//Icon for a filename, chosen by extension (case-insensitive).
export const iconForFile = (filename) => {
    const dot = filename.lastIndexOf('.');
    const ext = dot >= 0 ? filename.substring(dot + 1).toLowerCase() : '';

    switch (ext) {
        case 'bag': return <BagFileIcon />;
        case 'yaml':
        case 'yml': return <YamlFileIcon />;
        case 'mp4': return <Mp4FileIcon />;
        case 'ts': return <TsFileIcon />;
        case 'las': return <LasFileIcon />;
        case 'ply': return <PlyFileIcon />;
        default: return <GenericFileIcon />;
    }
};
